/* compare.js: the /compare/ matchup page.

   The reader picks the subject, so this page is assembled in the browser from
   /assets/data/compare-index.json (written at build time by
   src/compare-index-json.njk). Nothing here knows an artist id, a score
   threshold or a tier name: every one of those comes out of the index, which is
   how the page stays right as the roster grows.

   The whole app starts with the `hidden` attribute set in the template and is
   revealed here, so a reader with scripting off sees the <noscript> explanation
   rather than an empty shell. Values that CSS needs (gauge sweep, bar widths,
   bar heights) travel as data- attributes and are written to custom properties
   after insertion, so no markup carries a style attribute. */
(function () {
	"use strict";

	var app = document.getElementById("cmpApp");
	if (!app) return;

	var INDEX_URL = "/assets/data/compare-index.json";
	// Floor for the computed fallback pair further down. Thin catalogs are held out
	// of it for the same reason the home leaderboards hold them out: a 10-track entry
	// at 0% is a data gap wearing a number.
	var DEFAULT_MIN_TRACKS = 100;
	// Longest picker list rendered at once. The roster is searched in full; only
	// the drawn rows are capped.
	var MAX_OPTIONS = 50;

	var resultEl = document.getElementById("cmpResult");
	var randomBtn = document.getElementById("cmpRandom");
	var reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
	// The picker rows print the score only on a dev build: on the live site it hands
	// the reader the answer before the matchup is drawn. compare.njk sets data-dev
	// inside the same env gate the rest of the site uses, so nothing here has to look
	// at the hostname.
	var showScore = app.hasAttribute("data-dev");

	var sides = {
		a: {
			key: "a",
			input: document.getElementById("cmpSearchA"),
			list: document.getElementById("cmpListA"),
			count: document.getElementById("cmpCountA"),
			row: null
		},
		b: {
			key: "b",
			input: document.getElementById("cmpSearchB"),
			list: document.getElementById("cmpListB"),
			count: document.getElementById("cmpCountB"),
			row: null
		}
	};

	// Held in alphabetical order, sorted once in start(). Everything that walks the
	// roster reads it in that order, so the pickers need no sort of their own.
	var rows = [];
	// Null prototype: artist ids come from data, and a plain object would answer
	// to "constructor" or "toString" with something that is not an artist.
	var byId = Object.create(null);
	// The curated rivalries out of the index file, resolved to roster rows.
	var curated = [];

	/* ── helpers ── */

	var ESCAPES = { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" };
	function esc(value) {
		return String(value == null ? "" : value).replace(/[&<>"']/g, function (ch) { return ESCAPES[ch]; });
	}
	function num(value) {
		return Number(value).toLocaleString("en-US");
	}
	function norm(value) {
		return String(value == null ? "" : value).toLowerCase().replace(/[^a-z0-9]+/g, "");
	}
	function plural(count, one, many) {
		return count === 1 ? one : many;
	}
	// analytics.js ships on production builds only, so on a dev build gtag is absent
	// and this does nothing instead of throwing.
	function track(name, params) {
		if (typeof window.gtag === "function") window.gtag("event", name, params);
	}

	// Which comparisons send readers on to an artist page. Delegated: the result
	// band's links are re-rendered as innerHTML on every matchup.
	document.addEventListener("click", function (event) {
		var link = event.target.closest && event.target.closest("a[href^='/artist/']");
		if (!link) return;
		var id = decodeURIComponent(link.getAttribute("href").split("/")[2] || "");
		if (id) track("breakdown_click", { artist_id: id });
	});

	/* ── picker ── */

	function optionHtml(row, side) {
		var selected = side.row && side.row.id === row.id;
		return '<li><button type="button" class="cmp-opt is-' + esc(row.cls) + '" data-id="' + esc(row.id) + '"' +
			(selected ? ' aria-current="true"' : "") + ">" +
			'<span class="cmp-opt__name">' + esc(row.name) + "</span>" +
			'<span class="cmp-opt__meta">' + num(row.tracks) + " tracks - " + num(row.albums) + " " +
				plural(row.albums, "album", "albums") + "</span>" +
			(showScore ? '<span class="cmp-opt__score">' + row.score + "%</span>" : "") +
			"</button></li>";
	}

	// Starts-with beats contains, so typing "be" puts Beyonce and The Beatles above
	// an artist with "be" buried mid-name. Inside each of those two groups the order
	// is whatever order rows is in, which start() sets to alphabetical, so an empty
	// box lists the roster A to Z and a typed one stays alphabetical within its rank.
	function search(query) {
		if (!query) return rows.slice();
		var starts = [];
		var inside = [];
		for (var i = 0; i < rows.length; i++) {
			var at = rows[i].search.indexOf(query);
			if (at === 0) starts.push(rows[i]);
			else if (at > 0) inside.push(rows[i]);
		}
		return starts.concat(inside);
	}

	// Names visitors type that match nothing - reported once per query per page
	// view (not per keystroke), a direct feed for the artist queue.
	var missedQueries = {};

	function renderList(side) {
		var query = norm(side.input.value);
		var matches = search(query);
		var shown = matches.slice(0, MAX_OPTIONS);
		var html = "";
		for (var i = 0; i < shown.length; i++) html += optionHtml(shown[i], side);
		side.list.innerHTML = html;
		if (!matches.length) {
			side.count.textContent = "No artist matches that.";
			if (query.length > 2 && !missedQueries[query]) {
				missedQueries[query] = true;
				track("picker_no_results", { missed_query: query });
			}
		} else if (matches.length > shown.length) {
			side.count.textContent = shown.length + " of " + num(matches.length) + " shown. Keep typing to narrow it.";
		} else {
			side.count.textContent = num(matches.length) + " " + plural(matches.length, "match", "matches");
		}
	}

	function openList(side) {
		renderList(side);
		side.list.hidden = false;
	}

	function closeList(side) {
		side.list.hidden = true;
		side.count.textContent = "";
	}

	function closeAllLists() {
		closeList(sides.a);
		closeList(sides.b);
	}

	function bindSide(side) {
		side.input.addEventListener("focus", function () { openList(side); });
		side.input.addEventListener("input", function () { openList(side); });
		side.input.addEventListener("keydown", function (event) {
			if (event.key === "Escape") {
				closeList(side);
				return;
			}
			if (event.key === "ArrowDown") {
				var first = side.list.querySelector(".cmp-opt");
				if (first) {
					event.preventDefault();
					first.focus();
				}
				return;
			}
			if (event.key === "Enter") {
				var top = side.list.querySelector(".cmp-opt");
				if (top) {
					event.preventDefault();
					choose(side, top.getAttribute("data-id"));
				}
			}
		});
		side.list.addEventListener("click", function (event) {
			var button = event.target.closest(".cmp-opt");
			if (button) choose(side, button.getAttribute("data-id"));
		});
		side.list.addEventListener("keydown", function (event) {
			var button = event.target.closest(".cmp-opt");
			if (!button) return;
			var buttons = Array.prototype.slice.call(side.list.querySelectorAll(".cmp-opt"));
			var at = buttons.indexOf(button);
			if (event.key === "ArrowDown" && at < buttons.length - 1) {
				event.preventDefault();
				buttons[at + 1].focus();
			} else if (event.key === "ArrowUp") {
				event.preventDefault();
				if (at === 0) side.input.focus();
				else buttons[at - 1].focus();
			} else if (event.key === "Escape") {
				closeList(side);
				side.input.focus();
			}
		});
	}

	document.addEventListener("click", function (event) {
		var target = event.target;
		if (!target || !target.closest || !target.closest(".cmp-pick")) closeAllLists();
	});

	/* ── selection ── */

	function choose(side, id) {
		var row = byId[id];
		if (!row) return;
		var other = side.key === "a" ? sides.b : sides.a;
		// The same artist on both sides has nothing to compare, so the other slot
		// steps aside to whoever the picked artist displaced.
		if (other.row && other.row.id === row.id && side.row) other.row = side.row;
		side.row = row;
		closeList(side);
		side.input.value = "";
		track("artist_select", { picker_side: side.key, artist_id: row.id });
		commit();
	}

	function commit() {
		syncUrl();
		renderMatchup();
	}

	function syncUrl() {
		if (!sides.a.row || !sides.b.row || !window.history || !window.history.replaceState) return;
		var url = window.location.pathname + "?a=" + encodeURIComponent(sides.a.row.id) + "&b=" + encodeURIComponent(sides.b.row.id);
		window.history.replaceState(null, "", url + window.location.hash);
	}

	// The curated rivalries, read off the index file as [idA, idB] and turned into
	// row pairs. The build already dropped any pair whose two artists are not both
	// on the roster, so a miss here means a scoped dev build.
	function loadCurated(list) {
		curated = [];
		for (var i = 0; i < (list || []).length; i++) {
			var a = byId[list[i][0]];
			var b = byId[list[i][1]];
			if (a && b && a !== b) curated.push([a, b]);
		}
	}

	// One curated pair at random, never the one already on screen, so a click on
	// Randomize always changes something even though the list is short.
	function curatedPair() {
		if (!curated.length) return null;
		var pool = curated;
		if (curated.length > 1 && sides.a.row && sides.b.row) {
			var here = sides.a.row.id + "|" + sides.b.row.id;
			var flipped = sides.b.row.id + "|" + sides.a.row.id;
			pool = curated.filter(function (pair) {
				var key = pair[0].id + "|" + pair[1].id;
				return key !== here && key !== flipped;
			});
			if (!pool.length) pool = curated;
		}
		return pool[Math.floor(Math.random() * pool.length)];
	}

	// Any two artists on the roster. Only reached when the curated list cannot
	// answer, which on a full build means never.
	function rosterPair() {
		if (rows.length < 2) return null;
		var first = Math.floor(Math.random() * rows.length);
		var second = Math.floor(Math.random() * (rows.length - 1));
		if (second >= first) second += 1;
		return [rows[first], rows[second]];
	}

	// Randomize draws from the curated list rather than the whole roster. Two names
	// pulled out of 400 are almost always two acts nobody has ever argued about, and
	// the button is here to hand the reader a matchup worth reading, not to prove the
	// roster is large. Any other pairing is still one search away in the pickers.
	function randomPair() {
		var pair = curatedPair() || rosterPair();
		if (!pair) return;
		sides.a.row = pair[0];
		sides.b.row = pair[1];
		track("randomize_click", { page_area: "compare", artist_a: pair[0].id, artist_b: pair[1].id });
		commit();
	}

	// Widest score gap among catalogs over the floor. True, and a dud to read: it
	// lands the top of the board against the bottom of it, which is a statistical
	// edge rather than an argument. Kept as the fallback for a build whose roster is
	// too scoped to carry a curated pair.
	function widestGapPair() {
		var deep = rows.filter(function (row) { return row.tracks >= DEFAULT_MIN_TRACKS; });
		var pool = deep.length >= 2 ? deep : rows;
		if (pool.length < 2) return null;
		var ranked = pool.slice().sort(function (x, y) { return y.score - x.score || y.tracks - x.tracks; });
		return [ranked[0], ranked[ranked.length - 1]];
	}

	// What the page opens on with no ?a= and ?b= in the address: a curated rivalry,
	// drawn fresh on every load, then the computed pair, then the first two rows.
	function defaultPair() {
		return curatedPair() || widestGapPair() || (rows.length > 1 ? [rows[0], rows[1]] : null);
	}

	// A and B trade places. Named rather than inline because the control that calls
	// it is drawn and thrown away with the matchup.
	//
	// The move is animated as a FLIP: measure where the two cards sit, arc them
	// through each other's slot, then re-render at the far end. They fade out as
	// they land and the fresh pair fades in, which hides the one thing a pure
	// position swap cannot carry - each side owns a fixed accent (A blue, B purple),
	// so a travelling card has to change colour at the moment it arrives.
	var swapBusy = false;
	function swapSides() {
		if (!sides.a.row || !sides.b.row || swapBusy) return;
		var cardA = resultEl.querySelector('.cmp-card--a');
		var cardB = resultEl.querySelector('.cmp-card--b');
		var icon = resultEl.querySelector('.cmp-swap__txt .icon');
		
		function apply() {
			var held = sides.a.row;
			sides.a.row = sides.b.row;
			sides.b.row = held;
			track("swap_sides", { artist_a: sides.a.row.id, artist_b: sides.b.row.id });
			commit();
		}
		
		// No cards yet, no Web Animations, or the reader asked for less motion:
		// swap outright, exactly as this did before the animation existed.
		var still = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
		if (!cardA || !cardB || still || typeof cardA.animate !== 'function') { apply(); return; }
		
		var ra = cardA.getBoundingClientRect();
		var rb = cardB.getBoundingClientRect();
		var dx = rb.left - ra.left;
		var dy = rb.top - ra.top;
		if (!dx && !dy) { apply(); return; }
		// Side by side above 860px, stacked below it. Whichever axis actually
		// separates the two cards is the one they travel along; the other axis
		// supplies the bow that keeps them from sliding straight through each other.
		var sideways = Math.abs(dx) >= Math.abs(dy);
		var bow = sideways ? Math.max(18, ra.height * 0.14) : Math.max(18, ra.width * 0.08);
		
		function arc(sx, sy, lift) {
			return [
				{ transform: 'translate(0px, 0px) scale(1)', opacity: 1, offset: 0 },
				{ transform: 'translate(' + (sx / 2) + 'px, ' + (sy / 2 + lift) + 'px) scale(0.94)', opacity: 0.55, offset: 0.55 },
				{ transform: 'translate(' + sx + 'px, ' + sy + 'px) scale(0.97)', opacity: 0, offset: 1 }
			];
		}
		
		swapBusy = true;
		var out = { duration: 420, easing: 'cubic-bezier(0.4, 0, 0.2, 1)', fill: 'both' };
		// One card bows one way and the other bows back, so they pass alongside each
		// other rather than colliding in the middle.
		var flight = cardA.animate(arc(dx, dy, sideways ? -bow : bow), out);
		cardB.animate(arc(-dx, -dy, sideways ? bow : -bow), out);
		if (icon && typeof icon.animate === 'function') {
			icon.animate([{ transform: 'rotate(0deg)' }, { transform: 'rotate(180deg)' }],
				{ duration: 420, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' });
		}
		
		function land() {
			apply();
			swapBusy = false;
			var fresh = resultEl.querySelectorAll('.cmp-card');
			for (var i = 0; i < fresh.length; i++) {
				if (typeof fresh[i].animate !== 'function') continue;
				fresh[i].animate([{ opacity: 0, transform: 'scale(0.98)' }, { opacity: 1, transform: 'scale(1)' }],
					{ duration: 190, easing: 'ease-out' });
			}
		}
		// onfinish never fires while the tab is hidden, so a timer backs it up and a
		// flag keeps the two paths from both landing the same swap.
		var done = false;
		function once() { if (done) return; done = true; land(); }
		flight.onfinish = once;
		window.setTimeout(once, 520);
	}

	/* ── matchup ── */

	function metaLine(row) {
		var parts = [];
		if (row.genres && row.genres.length) parts.push(row.genres.slice(0, 3).join(", "));
		if (row.years) parts.push(row.years);
		return parts.join(" · ");
	}

	function sparkHtml(row) {
		if (!row.series || !row.series.length) {
			return '<div class="cmp-spark"><p class="cmp-spark__empty">No dated albums logged yet.</p></div>';
		}
		var bars = "";
		for (var i = 0; i < row.series.length; i++) {
			var entry = row.series[i];
			var score = entry[1];
			// Same shape as the artist page trajectory tooltip, and the same wording the
			// methodology list uses for an album whose credits are still missing.
			var label = (entry[3] ? entry[3] + " (" + entry[0] + ")" : entry[0]) + ", " +
				(score === null ? "credits not logged yet" : score + "% self-written");
			bars += '<span class="cmp-spark__bar is-' + esc(entry[2]) + '" data-h="' + (score === null ? 0 : score) +
				'" data-i="' + i + '" title="' + esc(label) + '"></span>';
		}
		var first = row.series[0][0];
		var last = row.series[row.series.length - 1][0];
		return '<div class="cmp-spark">' +
			'<p class="cmp-spark__title">Album by album</p>' +
			'<div class="cmp-spark__bars">' + bars + "</div>" +
			'<p class="cmp-spark__cap"><span>' + first + "</span><span>" + last + "</span></p>" +
			"</div>";
	}

	function cardHtml(row, key) {
		return '<article class="cmp-card cmp-card--' + key + " is-" + esc(row.cls) + '">' +
			'<h2 class="cmp-card__name"><a href="/artist/' + encodeURIComponent(row.id) + '/">' + esc(row.name) + "</a></h2>" +
			'<p class="cmp-card__meta">' + esc(metaLine(row)) + "</p>" +
			'<div class="cmp-gauge" data-val="' + row.score + '">' +
				'<div class="cmp-gauge__inner">' +
					'<p class="cmp-gauge__num">' + row.score + "<span>%</span></p>" +
					'<p class="cmp-gauge__cap">Authorship</p>' +
				"</div>" +
			"</div>" +
			'<p class="cmp-badge">' + esc(row.tier) + "</p>" +
			'<dl class="cmp-card__stats">' +
				"<div><dt>" + plural(row.albums, "Album", "Albums") + "</dt><dd>" + num(row.albums) + "</dd></div>" +
				"<div><dt>Tracks</dt><dd>" + num(row.tracks) + "</dd></div>" +
			"</dl>" +
			sparkHtml(row) +
			"</article>";
	}

	function barRowHtml(label, aValue, bValue) {
		return '<div class="cmp-bar-row">' +
			'<div class="cmp-bar cmp-bar--a"><span class="cmp-bar__val">' + aValue + '%</span>' +
				'<span class="cmp-bar__track"><span class="cmp-bar__fill" data-w="' + aValue + '"></span></span></div>' +
			'<span class="cmp-bar-row__label">' + label + "</span>" +
			'<div class="cmp-bar cmp-bar--b"><span class="cmp-bar__val">' + bValue + '%</span>' +
				'<span class="cmp-bar__track"><span class="cmp-bar__fill" data-w="' + bValue + '"></span></span></div>' +
			"</div>";
	}

	// Numbers only. The page states what the credits count and leaves the reasons
	// why to the artist pages.
	function verdictHtml(a, b) {
		var gap = Math.abs(a.score - b.score);
		var lead;
		var trail;
		var line;
		if (gap === 0) {
			line = "<b>" + esc(a.name) + "</b> and <b>" + esc(b.name) + "</b> both score " + a.score + "%.";
		} else {
			lead = a.score > b.score ? a : b;
			trail = lead === a ? b : a;
			line = "<b>" + esc(lead.name) + "</b> scores " + lead.score + "%, " + gap + " " +
				plural(gap, "point", "points") + " above <b>" + esc(trail.name) + "</b> on " + trail.score + "%.";
		}
		// Written as label-then-figures rather than a sentence about the act, so a
		// band name never has to agree with a verb.
		var sub = esc(a.name) + ": writing credits on " + num(a.written) + " of " + num(a.tracks) +
			" tracks, across " + num(a.albums) + " " + plural(a.albums, "album", "albums") + ". " +
			esc(b.name) + ": " + num(b.written) + " of " + num(b.tracks) + ", across " + num(b.albums) + ".";
		return '<p class="cmp-verdict">' + line + '<span class="cmp-verdict__sub">' + sub + "</span></p>";
	}

	function renderMatchup() {
		var a = sides.a.row;
		var b = sides.b.row;
		if (!a || !b) return;
		var gap = Math.abs(a.score - b.score);
		resultEl.innerHTML =
			'<div class="cmp-vs">' +
				cardHtml(a, "a") +
				'<div class="cmp-mid">' +
					'<button class="cmp-swap" type="button" aria-label="Swap sides">' +
						'<span class="cmp-swap__disc">VS</span>' +
						'<span class="cmp-swap__txt">' +
							'<svg class="icon" focusable="false" aria-hidden="true">' +
								'<use href="/assets/icons/sprite.svg#fa-arrows-left-right-to-line"></use></svg>' +
							"Swap sides</span>" +
					"</button>" +
					'<span class="cmp-mid__gap">' + gap + " pt gap</span>" +
				"</div>" +
				cardHtml(b, "b") +
			"</div>" +
			'<div class="cmp-bars">' +
				barRowHtml("Authorship", a.score, b.score) +
				barRowHtml("Wrote alone", a.sole, b.sole) +
				barRowHtml("Co-written", a.co, b.co) +
				barRowHtml("Outside writers", a.outside, b.outside) +
			"</div>" +
			verdictHtml(a, b) +
			'<div class="cmp-jump">' +
				'<a href="/artist/' + encodeURIComponent(a.id) + '/">Full breakdown for ' + esc(a.name) + "</a>" +
				'<a href="/artist/' + encodeURIComponent(b.id) + '/">Full breakdown for ' + esc(b.name) + "</a>" +
			"</div>";
		applyValues();
	}

	// Custom properties are written here rather than in the markup, so the page
	// ships no style attributes. The gauges start at zero and are raised on the
	// next frame, which is what makes the ring sweep.
	function applyValues() {
		var bars = resultEl.querySelectorAll(".cmp-spark__bar");
		for (var i = 0; i < bars.length; i++) {
			bars[i].style.setProperty("--h", bars[i].getAttribute("data-h"));
			bars[i].style.setProperty("--i", bars[i].getAttribute("data-i"));
		}
		var fills = resultEl.querySelectorAll(".cmp-bar__fill");
		for (var j = 0; j < fills.length; j++) fills[j].style.setProperty("--w", fills[j].getAttribute("data-w"));
		var gauges = resultEl.querySelectorAll(".cmp-gauge");
		var raise = function () {
			for (var k = 0; k < gauges.length; k++) gauges[k].style.setProperty("--val", gauges[k].getAttribute("data-val"));
		};
		if (reducedMotion) raise();
		else window.requestAnimationFrame(function () { window.requestAnimationFrame(raise); });
	}

	/* ── boot ── */

	function readParams() {
		var params = new URLSearchParams(window.location.search);
		return { a: byId[params.get("a")] || null, b: byId[params.get("b")] || null };
	}

	function start(data) {
		rows = (data && data.artists) || [];
		if (rows.length < 2) {
			showError();
			return;
		}
		for (var i = 0; i < rows.length; i++) {
			rows[i].search = norm(rows[i].name);
			byId[rows[i].id] = rows[i];
		}

		// Sorted here rather than on every keystroke. localeCompare so an accented
		// name files where a reader looks for it instead of after Z.
		rows.sort(function (x, y) { return x.name.localeCompare(y.name, "en", { sensitivity: "base" }); });
		loadCurated(data && data.pairs);

		// An address that names both artists wins over the opening pair: a linked
		// matchup has to come up as the reader who sent it saw it.
		var wanted = readParams();
		var fallback = defaultPair();
		sides.a.row = wanted.a || (fallback && fallback[0]) || rows[0];
		sides.b.row = wanted.b || (fallback && fallback[1]) || rows[1];
		if (sides.a.row.id === sides.b.row.id) sides.b.row = rows[rows.indexOf(sides.a.row) === 0 ? 1 : 0];

		bindSide(sides.a);
		bindSide(sides.b);
		closeAllLists();
		if (randomBtn) randomBtn.addEventListener("click", randomPair);
		// Delegated. The swap control is drawn inside the matchup, and renderMatchup
		// replaces that whole block on every change, so a handler bound to the button
		// itself would stop working the first time it was used.
		resultEl.addEventListener("click", function (event) {
			if (event.target.closest && event.target.closest(".cmp-swap")) swapSides();
		});
		commit();
	}

	function showError() {
		resultEl.innerHTML = '<p class="cmp-error">The credit index did not load, so there is nothing to compare. ' +
			'<a class="cmp-link" href="/artists">Browse the artist directory</a> instead.</p>';
	}

	app.hidden = false;
	window.fetch(INDEX_URL, { credentials: "same-origin" })
		.then(function (response) {
			if (!response.ok) throw new Error("index " + response.status);
			return response.json();
		})
		.then(start)
		.catch(showError);
})();
